import SeoHead, { ORGANIZATION_LD } from '../components/SeoHead'
import { ShieldCheck, MapPin, Code2, Lock } from 'lucide-react'
import { SITE, mailto } from '../lib/site'

const VALUES = [
  {
    icon: ShieldCheck,
    title: 'Standartlara uygun',
    body: 'KamuSM, AKİS ve EBYS’nin de kullandığı nitelikli e-imza standartları için tasarlandı. Belgeleriniz kendi sunucunuzda kalabilir.',
  },
  {
    icon: Code2,
    title: 'Modern mühendislik',
    body: 'CAdES, XAdES, PAdES, PKCS#11 token ve RFC 3161 zaman damgası standartlarına uygun; sıfırdan, test edilebilir bir mimariyle yazıldı.',
  },
  {
    icon: Lock,
    title: 'Şeffaflık',
    body: 'Kullanım telemetrisi toplamıyoruz; verinizin nerede işlendiğini ve nasıl korunduğunu açıkça belgeliyoruz.',
  },
]

export default function HakkimizdaPage() {
  return (
    <>
      <SeoHead
        title={`Hakkımızda — ${SITE.company}`}
        description="WSOFT Yazılım, kurumların e-imza ihtiyaçları için modern yazılım çözümleri geliştirir. W.Sign'ın arkasındaki ekibi ve çalışma ilkelerini tanıyın."
        path="/hakkimizda"
        jsonLd={{ '@context': 'https://schema.org', ...ORGANIZATION_LD }}
      />

      <section className="container-page pt-16 pb-8 md:pt-20">
        <div className="max-w-2xl">
          <span className="badge">Hakkımızda</span>
          <h1 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
            Kurumların e-imza altyapısını modernleştiriyoruz.
          </h1>
          <p className="mt-5 text-lg text-[var(--color-fg-muted)]">
            {SITE.company}, kurumların elektronik imza, iletişim ve dijital dönüşüm
            ihtiyaçları için yazılım çözümleri geliştiren bir teknoloji firmasıdır.
            <strong className="text-[var(--color-fg)]"> W.Sign</strong>, firmamızın
            e-imza ürünüdür: nitelikli imza üretimi, doğrulama, zaman damgası ve
            kurumsal entegrasyon tek bir altyapıda.
          </p>
        </div>
      </section>

      <section className="container-page py-8">
        <div className="grid gap-4 md:grid-cols-3">
          {VALUES.map(v => (
            <div key={v.title} className="card">
              <v.icon size={22} className="text-[var(--color-accent)]" />
              <h2 className="mt-3 font-semibold">{v.title}</h2>
              <p className="mt-2 text-sm text-[var(--color-fg-muted)]">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-page py-12">
        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <h2 className="text-2xl font-semibold">Neden W.Sign’ı geliştirdik?</h2>
            <p className="mt-4 text-[var(--color-fg-muted)]">
              Kurumlar uzun süre e-imza için sınırlı, maliyeti öngörülemeyen ve modernleşmeyen
              çözümlerle çalışmak zorunda kaldı. Biz, aynı uluslararası standartları
              (ETSI EN 319 122 / 132 / 142, RFC 3161) modern, test edilebilir ve entegrasyonu
              kolay bir altyapıyla yeniden ele aldık.
            </p>
            <p className="mt-4 text-[var(--color-fg-muted)]">
              Bugün W.Sign; gerçek nitelikli e-imza kartlarıyla doğrulanmış, canlı bir imza ve
              doğrulama portalı olarak hizmet vermektedir.
            </p>
          </div>

          <div className="card self-start">
            <h2 className="font-semibold">Künye</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <Row label="Firma" value={SITE.company} />
              <Row label="Ürün" value="W.Sign — e-imza altyapısı" />
              <Row label="Konum" value={SITE.city} icon={MapPin} />
              <Row label="İletişim" value={SITE.email.general} href={mailto(SITE.email.general)} />
              {SITE.phone && <Row label="Telefon" value={SITE.phone} href={`tel:${SITE.phone.replace(/\s/g, '')}`} />}
              <Row label="Kurumsal site" value="wsoft.tr" href={SITE.corpUrl} external />
            </dl>
          </div>
        </div>
      </section>

      <section className="container-page pb-20">
        <div className="card flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold">Kurumunuz için değerlendirmek ister misiniz?</h2>
            <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
              Teknik ekibinizle 30 dakikalık bir tanıtım görüşmesi planlayalım.
            </p>
          </div>
          <a href={mailto(SITE.email.sales, 'W.Sign — tanıtım görüşmesi')} className="btn-primary shrink-0">
            İletişime geç
          </a>
        </div>
      </section>
    </>
  )
}

function Row({ label, value, href, external, icon: Icon }: {
  label: string; value: string; href?: string; external?: boolean
  icon?: React.ComponentType<{ size?: number; className?: string }>
}) {
  const content = (
    <span className="inline-flex items-center gap-1.5">
      {Icon && <Icon size={14} className="text-[var(--color-fg-dim)]" />}
      {value}
    </span>
  )
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-[var(--color-fg-dim)]">{label}</dt>
      <dd className="text-right font-medium text-[var(--color-fg)]">
        {href
          ? <a href={href} {...(external ? { target: '_blank', rel: 'noopener' } : {})} className="hover:text-[var(--color-accent)]">{content}</a>
          : content}
      </dd>
    </div>
  )
}
